import React, { useState } from 'react';
import type { LscBillingCadence } from '../types';
import {
  LSC_ANNUAL_SAVINGS_COPY,
  LSC_FOUNDER_BETA_ANNUAL_LABEL,
  LSC_FOUNDER_BETA_ANNUAL_PRICE,
  LSC_FOUNDER_BETA_MONTHLY_LABEL,
  LSC_FOUNDER_BETA_MONTHLY_PRICE,
} from '../billingCopy';
import { createLscCheckoutSession, LscApiError } from '../api';
import { rememberCheckoutLeagueId } from '../checkoutLeagueContext';

type CheckoutCadence = Extract<LscBillingCadence, 'monthly' | 'annual'>;

interface LeagueCheckoutActionsProps {
  leagueId: string;
}

const LeagueCheckoutActions: React.FC<LeagueCheckoutActionsProps> = ({ leagueId }) => {
  const [loadingCadence, setLoadingCadence] = useState<CheckoutCadence | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const startCheckout = async (cadence: CheckoutCadence) => {
    setLoadingCadence(cadence);
    setCheckoutError(null);
    try {
      rememberCheckoutLeagueId(leagueId);
      const result = await createLscCheckoutSession(cadence, leagueId);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      setCheckoutError('Checkout is unavailable right now. Please try again later.');
    } catch (err) {
      const message =
        err instanceof LscApiError
          ? err.message
          : 'Checkout is unavailable right now. Please try again later.';
      setCheckoutError(message);
    } finally {
      setLoadingCadence(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="lsc-league-checkout-actions">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="card space-y-3" data-testid="lsc-league-pricing-monthly-card">
          <div>
            <p className="text-sm text-secondary-400">Monthly</p>
            <p className="text-2xl font-bold text-white">
              {LSC_FOUNDER_BETA_MONTHLY_PRICE}
              <span className="text-base font-normal text-secondary-400">/month</span>
            </p>
            <p className="text-xs text-secondary-500">{LSC_FOUNDER_BETA_MONTHLY_LABEL}</p>
          </div>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => void startCheckout('monthly')}
            disabled={loadingCadence !== null}
            data-testid="lsc-league-checkout-monthly-cta"
          >
            {loadingCadence === 'monthly' ? 'Starting checkout…' : 'Subscribe monthly'}
          </button>
        </div>

        <div className="card space-y-3" data-testid="lsc-league-pricing-annual-card">
          <div>
            <p className="text-sm text-secondary-400">Annual</p>
            <p className="text-2xl font-bold text-white">
              {LSC_FOUNDER_BETA_ANNUAL_PRICE}
              <span className="text-base font-normal text-secondary-400">/year</span>
            </p>
            <p className="text-xs text-secondary-500">{LSC_FOUNDER_BETA_ANNUAL_LABEL}</p>
            <p className="text-xs text-primary-300 mt-1">{LSC_ANNUAL_SAVINGS_COPY}</p>
          </div>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => void startCheckout('annual')}
            disabled={loadingCadence !== null}
            data-testid="lsc-league-checkout-annual-cta"
          >
            {loadingCadence === 'annual' ? 'Starting checkout…' : 'Subscribe annually'}
          </button>
        </div>
      </div>

      {checkoutError && (
        <p className="text-sm text-red-400" role="alert" data-testid="lsc-league-checkout-error">
          {checkoutError}
        </p>
      )}
    </div>
  );
};

export default LeagueCheckoutActions;
